/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { regexEntitySwaggerToRegexEntityStore } from 'src/luis/core/services/entities/mappers';
import { createRegexEntityStore } from 'src/luis/core/stores/regexEntityStore';
import { RegexEntitySwagger } from 'src/luis/projects/entities/types/swagger/RegexEntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';

/**
 * Represents a service to perform CRUD operations for regex entity.
 */
export const getRegexEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @param regexEntityId An optional regex entity id to add.
     * @returns A url string to call.
     */
    const getUrl = (regexEntityId?: LuisId): string =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('regexEntities', `${regexEntityId || ''}`)
            .build();

    /**
     * Gets an intent by id.
     *
     * @param id The id of the regex entity to fetch.
     */
    const getEntity = async ({ id }: Pick<RegexEntityStore, 'id'>): Promise<RegexEntityStore> => {
        const { data: regexEntity } = await httpService.get<RegexEntitySwagger>(getUrl(id), apiOptions);

        return regexEntitySwaggerToRegexEntityStore(regexEntity);
    };

    /**
     * Creates a new regex entity.
     *
     * @param name The new regex entity name.
     * @param pattern The new regex entity pattern.
     * @returns A new regex entity store.
     */
    const createEntity = async ({ name, pattern: regexPattern }: Pick<RegexEntityStore, 'name' | 'pattern'>): Promise<RegexEntityStore> => {
        const { data: regexEntityId } = await httpService.post<string>(getUrl(), { name, regexPattern }, apiOptions);

        return createRegexEntityStore(regexEntityId, { name, pattern: regexPattern });
    };

    /**
     * Deletes the given regex entity.
     *
     * @param id The id of the entity to delete.
     */
    const deleteEntity = async ({ id }: Pick<RegexEntityStore, 'id'>): Promise<void> => {
        await httpService.delete(getUrl(id), apiOptions);
    };

    /**
     * Updates a regex entity.
     *
     * @param id The id of the regex entity to update.
     * @param name Optional: The new regex entity name.
     * @param pattern Optional: The new regex entity pattern.
     */
    const updateEntity = async ({
        id,
        name,
        pattern: regexPattern
    }: Partial<Pick<RegexEntityStore, 'name' | 'pattern'>> & Pick<RegexEntityStore, 'id'>): Promise<void> => {
        await httpService.put(getUrl(id), { name, regexPattern }, apiOptions);
    };

    return { getEntity, createEntity, deleteEntity, updateEntity };
});
